import React, { useEffect, useMemo, useState } from "react";
import { Card } from "../ui/Card";
import DateRangePicker from "../ui/DayPicker";
import { H2 } from "src/util/Typography";
import { cn } from "src/util/cn";
import NoDataFound from "../NoDataFound";
import { RenderIcon } from "src/assets/icons";
import { useDateRange } from "src/context/DateRangeContext";
import { StorageKey } from "src/util/Storage";
import CustomTooltip from "../ui/ToolTip";
import toast from "react-hot-toast";
import Toast from "../ui/Toast";
import CheckBoxIconURL from "src/assets/icons/Checkbox-ui.svg";

interface FilterSectionProps {
  selectedTimePeriod: any;
  domains: string[];
  mobileAppIds: string[];
  onApply: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  selectedTimePeriod,
  domains,
  mobileAppIds,
  onApply,
}) => {
  const {
    dateRange,
    setDateRange,
    selectedDomains,
    setSelectedDomains,
    selectedMobileAppIds,
    setSelectedMobileAppIds,
    setIsFilterOpen,
    setHasManualDomainSelection,
    setHasManualMobileAppIdSelection,
  } = useDateRange();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [isFilterDisabled, setIsFilterDisabled] = useState<boolean>(false);
  const [mobileAppIdSearchQuery, setMobileAppIdSearchQuery] = useState<string>("");
  const [allMobileAppIdsChecked, setAllMobileAppIdsChecked] = useState<boolean>(false);

  // Sync "all checked" state for domains
  useEffect(() => {
    if (domains?.length) {
      setAllChecked(selectedDomains?.length === domains?.length);
    }
  }, [selectedDomains, domains]);

  // Sync "all checked" state for mobile app IDs
  useEffect(() => {
    if (mobileAppIds?.length) {
      setAllMobileAppIdsChecked(selectedMobileAppIds?.length === mobileAppIds?.length);
    } else {
      setAllMobileAppIdsChecked(false);
    }
  }, [selectedMobileAppIds, mobileAppIds]);

  const toggleDomainSelection = (event, domain: string) => {
    if (event.target.checked) {
      if (selectedDomains.length >= 100) {
        toast.custom(
          (t) => (
            <Toast
              message="Select up to 100 domains at a time, or select all domains."
              type="error"
            />
          ),
          { position: "bottom-left", duration: 2000 }
        );
        return;
      }
      const newSelectedDomains = [...selectedDomains, domain];
      setSelectedDomains(newSelectedDomains);
      if (newSelectedDomains.length === filteredDomains.length) {
        setAllChecked(true);
      }
    } else {
      const finalDomainsList = selectedDomains.filter(
        (item) => item !== domain
      );
      setSelectedDomains([...finalDomainsList]);
      setAllChecked(false);
    }
    setHasManualDomainSelection(true);
  };

  const clearAllDomains = () => {
    setSelectedDomains([]);
    setAllChecked(false);
    setHasManualDomainSelection(true);
  };

  const selectAllDomains = (event: any) => {
    const isChecked = event.target.checked;
    setAllChecked(isChecked);
    if (isChecked) {
      setSelectedDomains(filteredDomains); // Select all available domains
    } else {
      setSelectedDomains([]); // Clear all selections
    }
    setHasManualDomainSelection(true);
  };

  const filteredDomains = useMemo(() => {
    return domains.filter((domain) =>
      domain.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [domains, searchQuery]);

  const toggleMobileAppIdSelection = (event, mobileAppId: string) => {
    if (event.target.checked) {
      if (selectedMobileAppIds.length >= 100) {
        toast.custom(
          (t) => (
            <Toast
              message="Select up to 100 mobile app IDs at a time, or select all mobile app IDs."
              type="error"
            />
          ),
          { position: "bottom-left", duration: 2000 }
        );
        return;
      }
      const newSelectedMobileAppIds = [...selectedMobileAppIds, mobileAppId];
      setSelectedMobileAppIds(newSelectedMobileAppIds);
      if (newSelectedMobileAppIds.length === filteredMobileAppIds.length) {
        setAllMobileAppIdsChecked(true);
      }
    } else {
      const finalMobileAppIdsList = selectedMobileAppIds.filter(
        (item) => item !== mobileAppId
      );
      setSelectedMobileAppIds([...finalMobileAppIdsList]);
      setAllMobileAppIdsChecked(false);
    }
    setHasManualMobileAppIdSelection(true);
  };

  const clearAllMobileAppIds = () => {
    setSelectedMobileAppIds([]);
    setAllMobileAppIdsChecked(false);
    setHasManualMobileAppIdSelection(true);
  };

  const selectAllMobileAppIds = (event: any) => {
    const isChecked = event.target.checked;
    setAllMobileAppIdsChecked(isChecked);
    if (isChecked) {
      setSelectedMobileAppIds(filteredMobileAppIds);
    } else {
      setSelectedMobileAppIds([]);
    }
    setHasManualMobileAppIdSelection(true);
  };

  const filteredMobileAppIds = useMemo(() => {
    return mobileAppIds.filter((mobileAppId) =>
      mobileAppId.toLowerCase().includes(mobileAppIdSearchQuery.toLowerCase())
    );
  }, [mobileAppIds, mobileAppIdSearchQuery]);


  return (
    <>
      <Card
        // padding="large"
        className="flex flex-col h-[90vh]  my-4 px-[10px] "
      >
        <div>
          <div className="flex justify-between">
            <H2 className="uppercase mb-4">Filter</H2>
            <div
              onClick={() => setIsFilterOpen(false)}
              className="cursor-pointer  sm:hidden"
            >
              <RenderIcon type="xcloseIcon" width={24} height={24} />
            </div>
          </div>
          <div className="text-[#A1A1A1] text-[14px] leading-[18.18px] font-normal font-popins mb-4">
            {" "}
            Select Time Period
          </div>
          <div className="flex flex-col sm:flex-row  align-center  gap-4 ">
            <DateRangePicker
              setDateRange={setDateRange}
              // onApply={handleApply}
              dateRange={dateRange}
              // handleClearRange={handleClearRange}
              initialValues={true}
              disabledDate={localStorage.getItem(StorageKey.DisabledDate)}
              setIsFilterDisabled={setIsFilterDisabled}
            />
          </div>
          <div className="border-b border-[#474747] my-6"></div>
          <h3 className="text-[#A1A1A1] text-[14px] leading-[18.18px] font-normal font-popins mb-4 flex gap-4 items-center justify-between">
            All Domains{" "}
            <p className="bg-[#1E1E1E] rounded-[10px] px-[5px] py-[2px] text-white items-center mb-0 ml-auto">
              <span className="text-white">{selectedDomains.length}</span> <span className="text-[#717171]">of</span> <span className="text-white">{domains.length}</span> <span className="text-[#717171]">selected</span>
            </p>
          </h3>
          <div className="mb-4">
            <div className="relative w-full">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 mt-1">
                <RenderIcon
                  type="searchIcon"
                  className="w-5 h-5 text-[#A1A1A1]"
                />
              </div>

              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#2A2A2A] text-white rounded-full h-[40px] focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-[#343434] cursor-pointer border-none"
              />
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 px-3">
          <div className="flex items-center">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      id={"all"}
                      className="mr-2 accent-indigo-500 bg-black appearance-none w-5 h-5 rounded-md border-solid border-[1px] border-[#717171] checked:bg-indigo-500"
                      checked={allChecked}
                      onChange={(event) => selectAllDomains(event)}
                    />

                    {allChecked && (
                      <RenderIcon
                        type="checkBoxIcon"
                        className="absolute top-0 left-0 w-5 h-5 text-white pointer-events-none"
                      />
                    )}
                  </div>
                  <label
                    htmlFor={"all"}
                    className=" text-[#D0D0D0] font-popins font-normal text-[14px] leading-[19.88px]"
                  >
                  Select All
                  </label>
                </div>
            <div
              onClick={clearAllDomains}
              className="text-[#A1A1A1] text-[14px] leading-[18.18px] font-normal font-popins bg-transparent cursor-pointer"
            >
              Clear All
            </div>
          </div>
          <div className="border-b border-[#474747] my-3"></div>

          <div className="h-[18vh] sm:h-[17vh] overflow-y-auto p-3 rounded mb-3">
            {filteredDomains.length === 0 ? (
              <NoDataFound compact />
            ) : (
              <>

                {filteredDomains.map((domain, index) => {
                  const checked = selectedDomains?.includes(domain);
                  return (
                    <div key={index} className="flex items-center mb-4">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          id={index.toString()}
                          className={`mr-2 appearance-none w-5 h-5 rounded-md border-solid ${
                          checked
                            ? "accent-indigo-500 bg-indigo-500 border-[1px] border-indigo-500"
                            : "border-[1px] border-[#717171] bg-transparent opacity-50"
                        }`}
                          checked={checked}
                          onChange={(event) =>
                            toggleDomainSelection(event, domain)
                          }
                        />
                        {checked && (
                          <img
                            src={CheckBoxIconURL}
                            alt="Checked"
                            className="absolute top-0 left-0 w-5 h-5 text-white pointer-events-none"
                          />
                        )}{" "}
                      </div>

                      <label
                        htmlFor={index.toString()}
                        className={`text-[#D0D0D0] font-popins font-normal text-[14px] leading-[19.88px] cursor-pointer 
                      ${checked ? "" : "opacity-50"}`}
                      >
                        <CustomTooltip content={domain || "-"}>
                          <span>
                            {domain?.length > 23
                              ? `${domain.slice(0, 23)}...`
                              : domain}
                          </span>
                        </CustomTooltip>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className="border-b border-[#474747] my-3"></div>
          <h3 className="text-[#A1A1A1] text-[14px] leading-[18.18px] font-normal font-popins mb-4 flex gap-4 items-center justify-between">
            Mobile App IDs{" "}
            <p className="bg-[#1E1E1E] rounded-[10px] px-[5px] py-[2px] text-white items-center mb-0 ml-auto">
              <span className="text-white">{selectedMobileAppIds.length}</span> <span className="text-[#717171]">of</span> <span className="text-white">{mobileAppIds.length}</span> <span className="text-[#717171]">selected</span>
            </p>
          </h3>
          <div className="mb-4">
            <div className="relative w-full">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 mt-1">
                <RenderIcon
                  type="searchIcon"
                  className="w-5 h-5 text-[#A1A1A1]"
                />
              </div>

              <input
                type="text"
                placeholder="Search"
                value={mobileAppIdSearchQuery}
                onChange={(e) => setMobileAppIdSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#2A2A2A] text-white rounded-full h-[40px] focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-[#343434] cursor-pointer border-none"
              />
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 px-3">
          <div className="flex items-center">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      id={"all-mobile-app-ids"}
                      className="mr-2 accent-indigo-500 bg-black appearance-none w-5 h-5 rounded-md border-solid border-[1px] border-[#717171] checked:bg-indigo-500"
                      checked={allMobileAppIdsChecked}
                      onChange={(event) => selectAllMobileAppIds(event)}
                    />

                    {allMobileAppIdsChecked && (
                      <RenderIcon
                        type="checkBoxIcon"
                        className="absolute top-0 left-0 w-5 h-5 text-white pointer-events-none"
                      />
                    )}
                  </div>
                  <label
                    htmlFor={"all-mobile-app-ids"}
                    className=" text-[#D0D0D0] font-popins font-normal text-[14px] leading-[19.88px]"
                  >
                  Select All
                  </label>
                </div>
            <div
              onClick={clearAllMobileAppIds}
              className="text-[#A1A1A1] text-[14px] leading-[18.18px] font-normal font-popins bg-transparent cursor-pointer"
            >
              Clear All
            </div>
          </div>
          <div className="border-b border-[#474747] my-3"></div>

          <div className="h-[18vh] sm:h-[20vh] overflow-y-auto p-3 rounded mb-3">
            {filteredMobileAppIds.length === 0 ? (
              <NoDataFound compact />
            ) : (
              <>

                {filteredMobileAppIds.map((mobileAppId, index) => {
                  const checked = selectedMobileAppIds?.includes(mobileAppId);
                  return (
                    <div key={index} className="flex items-center mb-4">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          id={`mobile-app-id-${index}`}
                          className={`mr-2 appearance-none w-5 h-5 rounded-md border-solid ${
                          checked
                            ? "accent-indigo-500 bg-indigo-500 border-[1px] border-indigo-500"
                            : "border-[1px] border-[#717171] bg-transparent opacity-50"
                        }`}
                          checked={checked}
                          onChange={(event) =>
                            toggleMobileAppIdSelection(event, mobileAppId)
                          }
                        />
                        {checked && (
                          <img
                            src={CheckBoxIconURL}
                            alt="Checked"
                            className="absolute top-0 left-0 w-5 h-5 text-white pointer-events-none"
                          />
                        )}{" "}
                      </div>

                      <label
                        htmlFor={`mobile-app-id-${index}`}
                        className={`text-[#D0D0D0] font-popins font-normal text-[14px] leading-[19.88px] cursor-pointer
                      ${checked ? "" : "opacity-50"}`}
                      >
                        <CustomTooltip content={mobileAppId || "-"}>
                          <span>
                            {mobileAppId?.length > 23
                              ? `${mobileAppId.slice(0, 23)}...`
                              : mobileAppId}
                          </span>
                        </CustomTooltip>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <button
            onClick={onApply}
            disabled={isFilterDisabled || (selectedDomains?.length === 0 && selectedMobileAppIds?.length === 0)}
            className={cn(
              "mx-auto w-full sm:mx-0 px-6 py-2 bg-[#D0D0D0] text-black text-[13px] sm:text-[14px] font-popins rounded-full h-[40px] hover:bg-[#FFFFFF] cursor-pointer disabled:bg-[#717171] disabled:cursor-not-allowed"
            )}
          >
            Apply
          </button>
          {/* <Toaster position="bottom-left" reverseOrder={false} /> */}
        </div>
      </Card>
    </>
  );
};

export default FilterSection;
